import { cn } from '../lib/cn';
import { Spinner } from './spinner';

export type SwitchProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /**
   * Libellé accessible. Obligatoire : un interrupteur sans texte visible n'est
   * annoncé que par ce nom.
   */
  label: string;
  /** Masque le libellé à l'écran sans le retirer de l'arbre d'accessibilité. */
  hideLabel?: boolean;
  disabled?: boolean;
  /** Affiche un indicateur d'attente et bloque l'interaction. */
  loading?: boolean;
  /** Explique pourquoi l'interrupteur est bloqué (infobulle native). */
  title?: string;
  /** Couleur à l'état activé. `gold` marque un accès hors du cycle commercial normal. */
  tone?: 'brand' | 'gold';
  className?: string;
};

const PISTE_ACTIVEE = { brand: 'bg-brand-600', gold: 'bg-gold-500' } as const;

/**
 * Interrupteur à deux états. Rendu en `role="switch"` plutôt qu'en case à
 * cocher : la bascule est appliquée immédiatement, sans validation de
 * formulaire, et les lecteurs d'écran annoncent « activé / désactivé ».
 */
export function Switch({
  checked,
  onChange,
  label,
  hideLabel = false,
  disabled = false,
  loading = false,
  title,
  tone = 'brand',
  className,
}: SwitchProps) {
  const bloque = disabled || loading;

  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={hideLabel ? label : undefined}
        aria-busy={loading || undefined}
        disabled={bloque}
        title={title}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/30 focus-visible:ring-offset-2',
          checked ? PISTE_ACTIVEE[tone] : 'bg-border-input/40',
          bloque ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'flex size-5 items-center justify-center rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-[22px]' : 'translate-x-0.5',
          )}
        >
          {loading ? <Spinner className="size-3 text-brand-600" /> : null}
        </span>
      </button>

      {hideLabel ? null : <span className="text-sm text-content-muted">{label}</span>}
    </span>
  );
}
